import React, { useState, useRef, useEffect } from "react";
import Fade from "react-reveal/Fade";
import AliceCarousel from "react-alice-carousel";

/**
 * Section "Vos Réalisations" - Affiche un carrousel automatique des réalisations clients
 */

// Configuration du carrousel responsive (partagée par les 3 carrousels)
const responsive = {
  0: { items: 1 },
  576: { items: 2 },
  768: { items: 3 },
  992: { items: 3 },
};

interface AutoLoopCarouselProps {
  items: React.ReactNode[];
  categoryKey: string;
  intervalMs?: number;
  onExposeRef?: (advance: () => void) => void;
}

/**
 * Carrousel avec défilement automatique "en boucle", sans utiliser le prop
 * `infinite` d'AliceCarousel.
 *
 * Pourquoi : en mode `infinite`, AliceCarousel clone certains slides aux
 * extrémités pour pouvoir boucler en douceur. Ce clonage peut s'afficher
 * visuellement pendant la transition, donnant l'impression qu'une même
 * image/produit apparaît deux fois en même temps — quel que soit le nombre
 * d'images dans la catégorie (le bug se produit même avec beaucoup d'images).
 *
 * Solution : on gère nous-mêmes le défilement avec un simple intervalle.
 * On avance normalement (slideNext) et, une fois arrivé à la dernière
 * position, on force un remount du carrousel à l'index 0 (via un changement
 * de `key`). Un remount démarre instantanément sur le premier slide, sans
 * animation ni clonage, donc aucun doublon visuel n'est possible.
 */
const AutoLoopCarousel: React.FC<AutoLoopCarouselProps> = ({
  items,
  categoryKey,
  intervalMs = 3000,
  onExposeRef,
}) => {
  const carouselRef = useRef<AliceCarousel>(null);
  const activeIndexRef = useRef(0);
  const [resetCycle, setResetCycle] = useState(0);

  useEffect(() => {
    activeIndexRef.current = 0;
  }, [resetCycle]);

  useEffect(() => {
    if (!onExposeRef) return;
    onExposeRef(() => {
      const isAtEnd = activeIndexRef.current >= items.length - 1;
      if (isAtEnd) {
        setResetCycle((c) => c + 1);
      } else if (carouselRef.current) {
        carouselRef.current.slideNext();
      }
    });
  }, [items.length, onExposeRef]);

  useEffect(() => {
    const timer = setInterval(() => {
      const isAtEnd = activeIndexRef.current >= items.length - 1;
      if (isAtEnd) {
        // Remount instantané sur le premier slide : pas d'animation,
        // pas de clonage, donc pas de doublon visuel possible.
        setResetCycle((c) => c + 1);
      } else if (carouselRef.current) {
        carouselRef.current.slideNext();
      }
    }, intervalMs);
    return () => clearInterval(timer);
  }, [items.length, intervalMs]);

  return (
    <AliceCarousel
      key={`${categoryKey}-${resetCycle}`}
      ref={carouselRef}
      mouseTracking
      touchTracking
      items={items}
      responsive={responsive}
      infinite={false}
      disableButtonsControls
      disableDotsControls
      animationDuration={1000}
      onSlideChanged={(e: { item: number }) => {
        activeIndexRef.current = e.item;
      }}
    />
  );
};

const RealisationsSection: React.FC = () => {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  // Fonctions "avancer d'un cran", exposées par chaque AutoLoopCarousel,
  // utilisées pour reprendre le défilement après la fermeture du zoom.
  const advanceGresCerameRef = useRef<() => void>(() => {});
  const advanceAdhesifsRef = useRef<() => void>(() => {});
  const advanceCimentRef = useRef<() => void>(() => {});

  // Liste des images de réalisations - Carreaux Grès Cérame
  const imagesGresCerame = [
    "/images/CARREAUX GRES CERAME 1.jpeg",
    "/images/CARREAUX GRES CERAME 2.jpeg",
    "/images/CARREAUX GRES CERAME 3.jpeg",
    "/images/CARREAUX GRES CERAME 4.jpeg",
    "/images/CARREAUX GRES CERAME 5.jpeg",
    "/images/CARREAUX GRES CERAME 6.jpeg",
    "/images/CARREAUX GRES CERAME 7.jpeg",
    "/images/CARREAUX GRES CERAME 8.jpeg",
    "/images/CARREAUX GRES CERAME 9.jpeg",
    "/images/CARREAUX GRES CERAME 10.jpeg",
    "/images/CARREAUX GRES CERAME 11.png",
    "/images/CARREAUX GRES CERAME 12.png",
    "/images/CARREAUX GRES CERAME 13.png",
    "/images/CARREAUX GRES CERAME 14.png",
  ];

  // Liste des images de réalisations - Carreaux Adhésifs
  const imagesAdhesifs = [
    "/images/CARREAUX ADHESIFS 1.jpeg",
    "/images/CARREAUX ADHESIFS 2.jpeg",
    "/images/CARREAUX ADHESIFS 3.jpeg",
    "/images/CARREAUX ADHESIFS 4.jpeg",
    "/images/CARREAUX ADHESIFS 5.jpeg",
    "/images/CARREAUX ADHESIFS 6.png",
  ];

  // Liste des images de réalisations - Carreaux de Ciment
  const imagesCiment = [
    "/images/CARREAUS DE CIMENT 1.jpeg",
    "/images/CARREAUS DE CIMENT 2.jpeg",
    "/images/CARREAUS DE CIMENT 3.jpeg",
    "/images/CARREAUS DE CIMENT 4.jpeg",
    "/images/CARREAUS DE CIMENT 5.jpeg",
    "/images/CARREAUS DE CIMENT 6.jpeg",
    "/images/CARREAUS DE CIMENT 7.jpeg",
  ];

  const handleImageClick = (imageSrc: string, event: React.MouseEvent) => {
    // Empêcher la propagation pour ne pas interrompre le carrousel
    event.stopPropagation();
    setZoomedImage(imageSrc);
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
    // Reprendre le défilement automatique après fermeture
    advanceGresCerameRef.current();
    advanceAdhesifsRef.current();
    advanceCimentRef.current();
  };

  // Items du carrousel - Carreaux Grès Cérame
  const itemsGresCerame = imagesGresCerame.map((image, index) => (
    <div key={index} className="realisation-item">
      <img
        src={image}
        alt={`Réalisation Grès Cérame ${index + 1} - Yeksina Home`}
        className="realisation-image"
        loading="lazy"
        onClick={(e) => handleImageClick(image, e)}
        style={{ cursor: "pointer" }}
      />
    </div>
  ));

  // Items du carrousel - Carreaux Adhésifs
  const itemsAdhesifs = imagesAdhesifs.map((image, index) => (
    <div key={index} className="realisation-item">
      <img
        src={image}
        alt={`Réalisation Adhésifs ${index + 1} - Yeksina Home`}
        className="realisation-image"
        loading="lazy"
        onClick={(e) => handleImageClick(image, e)}
        style={{ cursor: "pointer" }}
      />
    </div>
  ));

  // Items du carrousel - Carreaux de Ciment
  const itemsCiment = imagesCiment.map((image, index) => (
    <div key={index} className="realisation-item">
      <img
        src={image}
        alt={`Réalisation Ciment ${index + 1} - Yeksina Home`}
        className="realisation-image"
        loading="lazy"
        onClick={(e) => handleImageClick(image, e)}
        style={{ cursor: "pointer" }}
      />
    </div>
  ));

  return (
    <section className="realisations-section my-5">
      <div className="container-easy-market">
        <div className="realisations-content">
          <Fade bottom>
            <h2 className="realisations-title">VOS REALISATIONS</h2>
          </Fade>
          <Fade bottom delay={100}>
            <p className="realisations-slogan">La touche finale qui change tout !</p>
          </Fade>
          <Fade bottom delay={200}>
            <p className="realisations-description">
              Nos clients ont du talent, et ils le prouvent. Explorez les photos de leurs poses et voyez par vous-même la qualité et le rendu exceptionnel du carrelage YEKSINA HOME une fois installé. Vous aussi, vous avez embelli votre maison avec nos produits ? Envoyez-nous vos photos pour apparaître ici ! Un simple message WhatsApp suffit 🙏
            </p>
          </Fade>
          <Fade bottom delay={300}>
            <h3 className="realisations-category-title">CARREAUX GRÈS CÉRAME</h3>
          </Fade>
          <Fade bottom delay={400}>
            <div className="realisations-carousel-wrapper">
              <AutoLoopCarousel
                items={itemsGresCerame}
                categoryKey="gres-cerame"
                onExposeRef={(advance) => {
                  advanceGresCerameRef.current = advance;
                }}
              />
            </div>
          </Fade>
          <Fade bottom delay={500}>
            <h3 className="realisations-category-title">CARREAUX ADHÉSIFS</h3>
          </Fade>
          <Fade bottom delay={600}>
            <div className="realisations-carousel-wrapper">
              <AutoLoopCarousel
                items={itemsAdhesifs}
                categoryKey="adhesifs"
                onExposeRef={(advance) => {
                  advanceAdhesifsRef.current = advance;
                }}
              />
            </div>
          </Fade>
          <Fade bottom delay={700}>
            <h3 className="realisations-category-title">CARREAUX DE CIMENT</h3>
          </Fade>
          <Fade bottom delay={800}>
            <div className="realisations-carousel-wrapper">
              <AutoLoopCarousel
                items={itemsCiment}
                categoryKey="ciment"
                onExposeRef={(advance) => {
                  advanceCimentRef.current = advance;
                }}
              />
            </div>
          </Fade>
        </div>
      </div>

      {/* Modal pour l'image zoomée */}
      {zoomedImage && (
        <div className="realisation-zoom-overlay" onClick={handleCloseZoom}>
          <div className="realisation-zoom-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="realisation-zoom-close"
              onClick={handleCloseZoom}
              aria-label="Fermer l'image zoomée"
            >
              ×
            </button>
            <img
              src={zoomedImage}
              alt="Réalisation zoomée - Yeksina Home"
              className="realisation-zoom-image"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default RealisationsSection;